<style lang="scss">
.zoezi-show-payment-dialog {
    .list-unstyled {
        list-style: none;
    }

    tr.empty-row {
        height: 10px;
    }

    table.table-header tr td:last-child {
        padding-left: 10px;
    }

    table.table-header tr td:first-child, .bold {
        font-weight: bold;
    }
}

</style>

<template>
    <div>
        <v-expansion-panels v-model="expandedPanel">
            <v-expansion-panel class="no-elevation" >
                <v-expansion-panel-header>
                    <div class="zoezi-activity-section">
                        <h3>{{$translate('Coming payments')}}</h3>
                        <small>{{$translate('Your debts are listed here')}}</small>
                    </div>
                    <v-badge color="grey" :content="String(debts.length)"></v-badge>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <div v-if="debts.length == 0">
                        {{$translate('No coming payments')}}
                    </div>
                    <payment-card v-for="debt in debts" :key="debt.id" :item="debt" @content-click="showPayment(debt)">
                        <template #date-line>
                            <div style="font-size: 11px" :class="isOverdue(debt) ? 'red--text font-weight-bold' : 'grey--text'">{{isOverdue(debt) ? $translate('Overdue') : $translate('Due')}}: {{Date.formatFullShorter(debt.date)}}</div>
                        </template>
                        <template #amount>{{(debt.leftToPayWithFees || debt.amount) | price}}</template>
                        <template #actions>
                            <v-btn icon @click.stop="showPayment(debt)" color="primary" small>
                                <v-icon>mdi-information-outline</v-icon>
                            </v-btn>
                            <v-btn small :class="(isOverdue(debt) ? 'red' : 'primary') + ' mb-2 zoezi-button-thin'" :color="isOverdue(debt) ? 'red' : 'primary'" dark @click.stop="pay(debt)" v-if="debt.canPay">{{$translate('Pay now')}}</v-btn>
                            <v-btn v-if="canWithdraw(debt)" small color="error" class="mb-2 zoezi-button-thin" @click.stop="withdrawalEntry = debt">{{ $translate('Withdraw from purchase') }}</v-btn>
                        </template>
                    </payment-card>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <v-dialog v-if="showPayDialog" v-model="showPayDialog" scrollable max-width="850px" :fullscreen="$vuetify.breakpoint.mobile">
            <v-card tile elevation="0" class="z-no-x-scroll">
                <v-card-title class="headline pa-2 primary primarytext--text d-flex fill-width">
                    <v-spacer />
                    <v-btn icon @click="showPayDialog = false"><v-icon class="primary primarytext--text">mdi-close</v-icon></v-btn>
                </v-card-title>
                <zoezi-checkout class="zoezi-checkout" :items="payments" :legal-entity-id="paymentLegalEntityId" @almostdone="showPaymentDialog=false">
                    <template #donetext>
                        <div><span class="font-weight-bold">{{$translate('Thank you for your payment!')}}</span></div>
                    </template>
                    <template #donesummarytitle>
                        <div class="d-flex font-weight-bold">
                            <div class="flex-grow-1">{{$translate('You have paid')}}</div>
                        </div>
                    </template>
                </zoezi-checkout>
            </v-card>
        </v-dialog>

        <v-dialog v-if="showPaymentDialog" v-model="showPaymentDialog" scrollable max-width="850px">
            <v-card tile elevation="0" class="z-no-x-scroll zoezi-show-payment-dialog">
                <v-card-title class="headline pa-2 primary primarytext--text d-flex fill-width">
                    {{getMethodName(showingPayment)}}
                    <v-spacer />
                    <v-btn icon @click="showPaymentDialog = false"><v-icon class="primary primarytext--text">mdi-close</v-icon></v-btn>
                </v-card-title>

                <v-card-text>
                    <v-row>
                        <v-col md=6 cols=12>
                            <table class="table-header">
                                <tr><td>{{'Payment method' | translate}}</td><td>{{getMethodName(showingPayment)}}</td></tr>
                                <tr><td>{{'Status' | translate}}</td><td>{{showingPayment.status.capitalize() | translate}}</td></tr>
                                <tr v-if="showingPayment.dateInfo"><td>{{showingPayment.dateInfo | translate}}</td><td>{{showingPayment.date.yyyymmdd()}}</td></tr>
                                <tr class="empty-row"></tr>
                                <tr v-if="showingPayment.bankgiroNumber"><td>{{'BG-account' | translate}}</td><td>{{formatBankgiroNumber(showingPayment.bankgiroNumber)}}</td></tr>
                                <tr v-if="showingPayment.referenceNumber"><td>{{'OCR-number' | translate}}</td><td>{{showingPayment.referenceNumber}}</td></tr>
                                <tr><td>{{'Amount' | translate}}</td><td>{{showingPayment.amount | price}}</td></tr>
                                <tr v-if="showingPayment.reminderFee"><td>{{'Reminder fee' | translate}}</td><td>{{showingPayment.reminderFee.amount | price}}</td></tr>
                                <tr v-if="showingPayment.reminderRate"><td>{{'Reminder rate' | translate}}</td><td>{{showingPayment.reminderRate.amount | price}}</td></tr>
                                <tr v-if="showingPayment.alreadyPaidAmount"><td>{{$translate('Already paid')}}</td><td>{{showingPayment.alreadyPaidAmount | price}}</td></tr>
                                <tr v-if="showingPayment.credited_amount"><td>{{$translate('Credited amount')}}</td><td>{{showingPayment.credited_amount | price}}</td></tr>
                                <tr v-if="showingPayment.leftToPayWithFees"><td>{{'To pay' | translate}}</td><td class="bold">{{showingPayment.leftToPayWithFees | price}}</td></tr>
                            </table>
                        </v-col>

                        <v-col md=6 cols=12>
                            <ul class="list-unstyled pl-0">
                                <li v-if="showingPayment.status == 'failed' && showingPayment.message"><p>{{showingPayment.message}}</p></li>
                                <li><p class="bold">{{'This payment contains the following products' | translate}}</p></li>
                                <li v-for="(item,index) in showingPayment.items" :key="index">
                                    {{item.name}}
                                    <withdrawal-status v-if="item.withdrawalRequest" :text="withdrawalRequestText(item.withdrawalRequest)" />
                                </li>
                            </ul>
                        </v-col>
                    </v-row>

                </v-card-text>

                <v-card-actions>
                    <v-btn v-if="showingPayment.canPay" color="primary" @click="pay(showingPayment)">{{ $translate('Pay') }}</v-btn>
                    <v-btn v-if="showingPayment.invoice_hash || showingPayment.reminder_id" color="primary" @click="getInvoice(showingPayment)" :loading="gettingInvoice">{{ $translate('Get invoice') }}</v-btn>
                    <v-spacer />
                </v-card-actions>
            </v-card>
        </v-dialog>

        <withdrawal-my-page-dialog v-if="withdrawalEntry" :entry="withdrawalEntry" @close="withdrawalEntry = null" @withdrawn="fetchDebts" />
    </div>
</template>

<script>
import { downloadFile, isExpoApp, withdrawalRequestText } from '../util';
import PaymentCard from '../components/PaymentCard.vue';
import WithdrawalStatus from '../components/WithdrawalStatus.vue';
import WithdrawalMyPageDialog from './WithdrawalMyPageDialog.vue';
export default {
    name: 'zoezi-coming-payments',

    components: { PaymentCard, WithdrawalStatus, WithdrawalMyPageDialog },

    zoezi: {
        title: 'Upcoming payments',
        icon: 'mdi-credit-card-clock-outline'
    },

    data: () => ({
        debts: [],
        unsubscribe: null,
        showPayDialog: false,
        showPaymentDialog: false,
        payments: [],
        paymentLegalEntityId: null,
        expandedPanel: 0,
        showingPayment: null,
        gettingInvoice: false,
        withdrawalEntry: null
    }),

    props: {
    },

    methods: {
        withdrawalRequestText,
        canWithdraw(entry) {
            return this.$api.hasFlag('flag-withdrawal-request') && (entry.items || []).some(item => item.canWithdraw);
        },
        isOverdue(debt) {
            const awaitingSend = debt.status === 'pending' && ['invoice',
                'directdebit', 'einvoice'].includes(debt.method);
            return !awaitingSend && debt.date.isPastDate();
        },
        fetchDebts() {
            this.$api.get('/api/memberapi/debts/get').then(x => {
                this.debts = x.map(d => ({
                    ...d,
                    date: Date.newFull(d.date),
                    dateInfo: d.method == 'directdebit' && d.status != 'failed' ? this.$translate('To be deducted by direct debit') : (d.status == 'pending' ? '' : this.$translate('Due date')),
                    name: d.name ? d.name : d.items.map(i => i.name).join(', ')
                })).sort((a, b) => Date.newFull(a.date) - Date.newFull(b.date));
                this.$emit('count', this.debts.length);
            });
        },
        getPaymentItems(debt) {
            let payments = [];
            payments.push({
                name: debt.name,
                date: debt.date,
                amount: debt.amount,
                payments: debt.payments,
                method: debt.method,
            });
            if (debt.leftToPay) {
                payments[0].amount = +debt.leftToPay;
            }

            if (debt.reminderFee) {
                payments.push({
                    name: debt.reminderFee.name,
                    amount: debt.reminderFee.amount,
                    payments: debt.reminderFee.payments,
                    date: debt.reminderFee.date,
                });
            }

            if (debt.reminderRate) {
                payments.push({
                    name: debt.reminderRate.name,
                    amount: debt.reminderRate.amount,
                    payments: debt.reminderRate.payments,
                    date: debt.reminderRate.date
                });
            }

            return payments.map(payment => {
                let name = payment.name;

                return {
                    name: name,
                    amount: payment.amount,
                    payments: payment.payments,
                    date: payment.date,
                    method: payment.method
                };
            });
        },
        pay(debt) {
            this.payments= [{payment: {payments: this.getPaymentItems(debt)}}];
            this.paymentLegalEntityId = debt.legal_entity_id || null;
            this.showPayDialog = true;
        },
        showPayment(debt) {
            this.showingPayment = debt;
            this.showPaymentDialog = true;
        },
        getMethodName(debt) {
            if (debt.method) {
                let method = debt.method == 'unknown' ? 'Pay on site' : debt.method;
                const translated = this.$translate(method);
                if (translated !== method) {
                    return translated.capitalize();
                }
                return debt.method_name || method;
            }
        },
        formatBankgiroNumber(x) {
            if (x) {
                x = String(x);
                if (x.length == 6) {
                    x = x.insertAt(3, '-');
                } else if (x.length >= 6) {
                    x = x.insertAt(x.length - 4, '-');
                }
            }

            return x;
        },
        getInvoice(debt) {
            this.gettingInvoice = true;
            let url, args;
            if (debt.reminder_id) {
                url = '/api/memberapi/invoice/reminder/get';
                args = {id: debt.reminder_id};
            } else {
                url = '/api/memberapi/invoice/get';
                args = {hash: debt.invoice_hash};
            }

            if (isExpoApp()) {
                let data = { api: { url: 'current', path: url, args }, type: 'pdf' };
                window.ReactNativeWebView.postMessage(JSON.stringify({type: 'openFile', data}));
                this.gettingInvoice = false;
            } else {
                this.$api.post(url, null, args).then(response => {
                    if (response.ok) {
                        response.blob().then(x => {
                            downloadFile({filename: `${this.$translate('Invoice')}.pdf`, data: x, type: 'application/pdf'});
                        });
                    }
                    this.gettingInvoice = false;
                });
            }

        }
    },

    mounted() {
        this.fetchDebts();
        this.unsubscribe = this.$root.$api.subscribe({type: 'payment', callback: () => this.fetchDebts()})
    },
    destroyed() {
        this.unsubscribe?.();
    }
}
</script>
